import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import api from "../api";

export default function Wishlist() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = user?.access;

  // ================================
  // GET PRODUCT IMAGE
  // ================================
  const getImage = (product) => {
    return (
      product?.sub_images?.[0]?.image ||
      product?.images?.[0]?.image ||
      product?.image ||
      product?.product_image ||
      "/placeholder.jpg"
    );
  };

  // ================================
  // GET PRODUCT PRICE
  // ================================
  const getPrice = (product) => {
    // Product has sizes
    if (Array.isArray(product?.sizes) && product.sizes.length > 0) {
      const prices = product.sizes
        .map((size) => Number(size?.price))
        .filter((price) => !Number.isNaN(price));

      if (prices.length > 0) {
        return Math.min(...prices);
      }
    }

    // Other possible price fields
    if (product?.price != null) {
      const price = Number(product.price);

      if (!Number.isNaN(price)) {
        return price;
      }
    }

    return null;
  };

  // ================================
  // LOAD WISHLIST
  // ================================
  useEffect(() => {
    const loadWishlist = async () => {
      try {
        setLoading(true);

        // ========================================
        // LOGGED-IN USER
        // ========================================
        if (token) {
          // Get favorites
          const favoritesRes = await api.get(
            "/api/favorites/",
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const backendFavorites = Array.isArray(
            favoritesRes.data
          )
            ? favoritesRes.data
            : [];

          console.log(
            "Favorites from backend:",
            backendFavorites
          );

          if (!backendFavorites.length) {
            setItems([]);
            return;
          }

          // ========================================
          // GET ALL PRODUCTS
          // ========================================
          const productsRes = await api.get(
            "/api/products/"
          );

          const products = Array.isArray(productsRes.data)
            ? productsRes.data
            : [];

          console.log(
            "Products from backend:",
            products
          );

          // ========================================
          // MATCH FAVORITE PRODUCT ID
          // WITH ACTUAL PRODUCT OBJECT
          // ========================================
          const wishlistItems = backendFavorites
            .map((favorite) => {
              const productId = favorite?.product;

              const product = products.find(
                (p) =>
                  String(p?.id) === String(productId)
              );

              if (!product) {
                console.warn(
                  `Product ${productId} from favorite was not found`
                );

                return null;
              }

              return {
                ...favorite,
                product,
                isLocal: false,
              };
            })
            .filter(Boolean);

          console.log(
            "Final wishlist items:",
            wishlistItems
          );

          setItems(wishlistItems);

          return;
        }

        // ========================================
        // LOGGED-OUT USER
        // ========================================
        const localFavs = JSON.parse(
          localStorage.getItem("favs") || "{}"
        );

        const favoriteIds = Object.keys(localFavs).filter(
          (id) => localFavs[id]
        );

        if (!favoriteIds.length) {
          setItems([]);
          return;
        }

        const productsRes = await api.get(
          "/api/products/"
        );

        const allProducts = Array.isArray(
          productsRes.data
        )
          ? productsRes.data
          : [];

        const localItems = favoriteIds
          .map((id) => {
            const product = allProducts.find(
              (p) =>
                String(p?.id) === String(id)
            );

            if (!product) {
              return null;
            }

            return {
              id: `local-${id}`,
              product,
              isLocal: true,
            };
          })
          .filter(Boolean);

        setItems(localItems);
      } catch (err) {
        console.error(
          "Wishlist error:",
          err.response?.data || err.message
        );

        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadWishlist();
  }, [token]);

  // ================================
  // REMOVE FAVORITE
  // ================================
  const removeItem = async (item, event) => {
    event?.stopPropagation();

    const productId = item?.product?.id;

    if (!productId) {
      return;
    }

    try {
      setRemovingId(productId);

      // ========================================
      // REMOVE FROM LOCAL STORAGE
      // ========================================
      const localFavs = JSON.parse(
        localStorage.getItem("favs") || "{}"
      );

      delete localFavs[productId];

      localStorage.setItem(
        "favs",
        JSON.stringify(localFavs)
      );

      // ========================================
      // REMOVE FROM UI
      // ========================================
      setTimeout(() => {
        setItems((prev) =>
          prev.filter(
            (i) =>
              String(i?.product?.id) !==
              String(productId)
          )
        );
      }, 250);

      // ========================================
      // REMOVE FROM BACKEND
      // ========================================
      if (token) {
        try {
          await api.post(
            "/api/favorites/toggle/",
            {
              product_id: productId,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        } catch (err) {
          console.error(
            "Failed to remove backend favorite:",
            err.response?.data || err.message
          );
        }
      }
    } catch (err) {
      console.error(
        "Remove favorite error:",
        err
      );
    } finally {
      setTimeout(() => {
        setRemovingId(null);
      }, 300);
    }
  };

  // ================================
  // LOADING
  // ================================
  if (loading) {
    return (
      <div className="min-h-[60vh] bg-[#F5F1E8]">
        <div
          className="
            max-w-7xl
            mx-auto
            px-4
            py-10
            grid
            grid-cols-1
            sm:grid-cols-2
            md:grid-cols-3
            lg:grid-cols-4
            gap-6
          "
        >
          {Array(8)
            .fill(0)
            .map((_, i) => (
              <div
                key={i}
                className="
                  h-80
                  rounded-2xl
                  bg-[#DDEDEA]
                  animate-pulse
                "
              />
            ))}
        </div>
      </div>
    );
  }

  // ================================
  // EMPTY
  // ================================
  if (!items.length) {
    return (
      <div
        className="
          min-h-[60vh]
          bg-[#F5F1E8]
          flex
          items-center
          justify-center
        "
      >
        <div className="text-center">
          <div
            className="
              w-16
              h-16
              mx-auto
              mb-4
              rounded-full
              bg-[#DCEFEB]
              flex
              items-center
              justify-center
            "
          >
            <span className="text-2xl text-[#087F73]">
              ♥
            </span>
          </div>

          <h2 className="text-2xl font-bold text-[#173F3A]">
            No favorites yet
          </h2>

          <p className="mt-2 text-[#7A827E]">
            Products you love will appear here.
          </p>
        </div>
      </div>
    );
  }

  // ================================
  // WISHLIST
  // ================================
  return (
    <div className="min-h-[60vh] bg-[#F5F1E8]">
      <div
        className="
          max-w-7xl
          mx-auto
          px-4
          py-10
          grid
          grid-cols-1
          sm:grid-cols-2
          md:grid-cols-3
          lg:grid-cols-4
          gap-6
        "
      >
        {items.map((item) => {
          const product = item?.product;

          if (!product?.id) {
            return null;
          }

          const productId = product.id;
          const price = getPrice(product);

          return (
            <div
              key={`${item.isLocal ? "local" : "backend"}-${productId}`}
              onClick={() =>
                navigate(`/product/${productId}`)
              }
              className={`
                relative
                overflow-hidden
                rounded-2xl
                shadow-md
                h-80
                group
                cursor-pointer
                bg-[#FFFDF8]
                border
                border-[#D8E7E3]
                transition-all
                duration-300
                hover:shadow-xl
                hover:-translate-y-1
                ${
                  removingId === productId
                    ? "opacity-0 scale-90"
                    : "opacity-100 scale-100"
                }
              `}
            >
              {/* IMAGE */}
              <img
                src={getImage(product)}
                className="
                  w-full
                  h-full
                  object-cover
                  group-hover:scale-110
                  transition
                  duration-500
                "
                alt={product.name || "Product"}
              />

              {/* IMAGE OVERLAY */}
              <div
                className="
                  absolute
                  inset-0
                  bg-gradient-to-t
                  from-[#073F39]/90
                  via-[#073F39]/15
                  to-transparent
                  pointer-events-none
                "
              />

              {/* REMOVE */}
              <button
                onClick={(event) =>
                  removeItem(item, event)
                }
                aria-label="Remove from favorites"
                className="
                  absolute
                  top-3
                  right-3
                  w-10
                  h-10
                  flex
                  items-center
                  justify-center
                  rounded-full
                  bg-[#FFFDF8]/95
                  backdrop-blur-md
                  text-[#087F73]
                  shadow-md
                  hover:bg-[#087F73]
                  hover:text-white
                  hover:scale-110
                  transition
                  z-10
                "
              >
                <FaTrash size={14} />
              </button>

              {/* PRODUCT INFO */}
              <div
                className="
                  absolute
                  bottom-0
                  left-0
                  right-0
                  p-4
                  text-white
                "
              >
                <h3
                  className="
                    text-sm
                    font-semibold
                    line-clamp-2
                  "
                >
                  {product.name || "Product"}
                </h3>

                {/* PRICE */}
                <p
                  className="
                    mt-1
                    text-lg
                    font-bold
                    text-[#8DD8CF]
                  "
                >
                  {price !== null
                    ? `$${price.toFixed(2)}`
                    : "Price unavailable"}
                </p>

                <p
                  className="
                    mt-1
                    text-xs
                    text-[#D6F1ED]
                  "
                >
                  View product details
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}